import React from 'react';
import {createGlobalStyle} from 'styled-components';
import {Transition, TransitionProps} from '../Transition';
import {getTimeouts} from '../utils';

export type FadeProps = Pick<
  TransitionProps,
  'mounted' | 'duration' | 'delay' | 'onUnmount'
>;

export const Fade: React.FC<React.PropsWithChildren<FadeProps>> = ({
  children,
  mounted,
  duration,
  delay,
  onUnmount,
}) => {
  const nodeRef = React.useRef(null);

  const timeouts = getTimeouts({duration, delay});
  const animationDuration = mounted
    ? timeouts.mountDuration
    : timeouts.unmountDuration;
  const animationDelay = mounted ? timeouts.mountDelay : timeouts.unmountDelay;

  const styles = {
    willChange: 'opacity',
    opacity: mounted ? 0 : 1,
    animation: `${
      mounted ? 'fadeEnter' : 'fadeExit'
    } ${animationDuration}ms ease-in-out ${animationDelay}ms forwards`,
  };

  return (
    <Transition
      nodeRef={nodeRef}
      mounted={mounted}
      duration={duration}
      delay={delay}
      onUnmount={onUnmount}
    >
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement, {
            style: {
              ...(child.props?.style ? child.props.style : {}),
              ...styles,
            },
            ref: nodeRef,
          });
        }
        return null;
      })}
      <Animations />
    </Transition>
  );
};

const Animations = createGlobalStyle`
  @keyframes fadeEnter {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes fadeExit {
    from { opacity: 1; }
    to { opacity: 0; }
  }
`;
